import React, { useState, useEffect } from "react";
import {
  ChevronLeft,
  LayoutGrid,
  ShoppingBag,
  Users,
  Wallet,
  Package,
  BarChart3,
  Settings,
  Database,
  X,
} from "lucide-react";
import NavItem from "./NavItem";
import { useNavigate } from "react-router-dom";
import { authService } from "../api/authService";
import logoAsalink from "../assets/img/Icon ASAlink Secondary.png";
import { useSidebarConfig } from "../contexts/SidebarConfigContext";
import { HasPermission } from "../hooks/usePermission";

export default function Sidebar({ isCollapsed, onToggle, activePage = "", isDrawerOnly = false }) {
  const navigate = useNavigate();
  const { config, mobileOpen, setMobileOpen } = useSidebarConfig();
  const [isHovering, setIsHovering] = useState(false);

  const [tenantName, setTenantName] = useState(() => {
    return localStorage.getItem("tenantName") || authService.getCurrentUser()?.tenant_name || "ASA Link";
  });
  const [tenantLogoUrl, setTenantLogoUrl] = useState(() => {
    const url = localStorage.getItem("tenantLogoUrl");
    return url && url !== "null" ? url : "";
  });
  const [logoFailed, setLogoFailed] = useState(false);

  useEffect(() => {
    const handleStorageChange = () => {
      setTenantName(localStorage.getItem("tenantName") || authService.getCurrentUser()?.tenant_name || "ASA Link");
      const url = localStorage.getItem("tenantLogoUrl");
      setTenantLogoUrl(url && url !== "null" ? url : "");
      setLogoFailed(false);
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("tenantUpdated", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("tenantUpdated", handleStorageChange);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await authService.logout();
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Logout error:", error);
      navigate("/login", { replace: true });
    }
  };

  // Effective collapsed: kalau hoverExpand aktif & sedang di-hover -> expand sementara
  // Di mobile drawer (mobileOpen = true), sidebar harus selalu terekspansi (tidak collapsed)
  const effectiveCollapsed = isCollapsed && !mobileOpen && !(config.hoverExpand && isHovering);

  // Mobile: kalau mobileHide aktif, sidebar disembunyikan kecuali mobileOpen=true
  const mobileHidden = config.mobileHide && !mobileOpen;

  const showToggleButton = config.clickToggle;

  const handleNavClick = () => {
    if (mobileOpen) setMobileOpen(false);
  };

  return (
    <>
      {/* Backdrop overlay (saat drawer terbuka di mobile atau mode isDrawerOnly di desktop) */}
      {((config.mobileHide && mobileOpen) || (isDrawerOnly && mobileOpen)) && (
        <div
          className="fixed inset-0 bg-black/40 z-50 pointer-events-auto"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <div
        onMouseEnter={() => !isDrawerOnly && config.hoverExpand && setIsHovering(true)}
        onMouseLeave={() => !isDrawerOnly && setIsHovering(false)}
        className={`bg-gradient-to-b from-teal-700 via-teal-700 to-teal-800 text-white flex flex-col fixed h-screen asalink-sidebar-height transition-all duration-300 z-50 shadow-xl shadow-teal-900/10 top-0 left-0 pointer-events-auto
          ${isDrawerOnly
            ? `w-56 md:w-60 ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`
            : `${effectiveCollapsed ? "w-16" : "w-48"} ${mobileHidden ? "-translate-x-full md:translate-x-0" : "translate-x-0"}`
          }`}>
        {/* Logo and Toggle */}
        {isDrawerOnly || !effectiveCollapsed ? (
          <div className="px-3 border-b border-white/10 flex items-center justify-between h-16">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-9 h-9 rounded-lg bg-white/15 backdrop-blur-sm border border-white/20 flex items-center justify-center flex-shrink-0 overflow-hidden">
                <img
                  src={logoAsalink}
                  alt={tenantName}
                  className="h-6 w-auto object-contain"
                />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-bold text-white leading-tight truncate">
                  ASA Link
                </div>
                <div className="text-[10px] text-teal-100/70 leading-tight truncate">
                  Point of Sale
                </div>
              </div>
            </div>
            {mobileOpen ? (
              <button
                onClick={() => setMobileOpen(false)}
                className="text-white/80 hover:text-white hover:bg-white/10 p-1.5 rounded-md transition-colors flex-shrink-0"
                title="Tutup Menu">
                <X className="w-4 h-4" />
              </button>
            ) : showToggleButton && !isDrawerOnly ? (
              <button
                onClick={onToggle}
                className="text-white/80 hover:text-white hover:bg-white/10 p-1.5 rounded-md transition-colors flex-shrink-0"
                title="Minimize">
                <ChevronLeft className="w-4 h-4" />
              </button>
            ) : null}
          </div>
        ) : (
          <div
            className={`px-2 border-b border-white/10 flex flex-col items-center justify-center h-16 transition-colors ${showToggleButton ? "cursor-pointer hover:bg-white/5" : ""}`}
            onClick={showToggleButton ? onToggle : undefined}
            title={showToggleButton ? "Expand" : ""}>
            <div className="w-9 h-9 rounded-lg bg-white/15 backdrop-blur-sm border border-white/20 flex items-center justify-center overflow-hidden">
              <img
                src={logoAsalink}
                alt={tenantName}
                className="h-6 w-auto object-contain"
              />
            </div>
          </div>
        )}

        {/* Navigation Menu */}
        <nav
          className="flex-1 py-5 space-y-1 px-2.5 overflow-y-auto"
          onClick={handleNavClick}>
          <NavItem
            label="Beranda"
            Icon={LayoutGrid}
            active={activePage === "beranda"}
            isCollapsed={effectiveCollapsed}
            to="/"
          />
          <HasPermission permission={["order.create", "order.index", "order.payment", "pos.create_trx"]}>
            <NavItem
              label="Penjualan"
              Icon={ShoppingBag}
              active={activePage === "penjualan"}
              isCollapsed={effectiveCollapsed}
              to="/penjualan"
            />
          </HasPermission>
          <HasPermission permission={["customer.index", "customer.create", "customer.update", "customer.delete", "customer.view"]}>
            <NavItem
              label="Pelanggan"
              Icon={Users}
              active={activePage === "pelanggan"}
              isCollapsed={effectiveCollapsed}
              to="/pelanggan"
            />
          </HasPermission>
          {/* Keuangan, Inventori, Master Data, Laporan, Konfigurasi usually restricted to Admin or specific perms */}
          {/* Currently Kasir doesn't have explicit permissions for these based on the mock list, so they will be hidden for Kasir */}
          <HasPermission permission={["finance.index", "pos.create_trx"]}>
            <NavItem
              label="Keuangan"
              Icon={Wallet}
              active={activePage === "keuangan"}
              isCollapsed={effectiveCollapsed}
              to="/keuangan"
            />
          </HasPermission>
          <HasPermission permission="inventory.index">
            <NavItem
              label="Inventori"
              Icon={Package}
              active={activePage === "inventori"}
              isCollapsed={effectiveCollapsed}
              to="/inventori"
            />
          </HasPermission>
          <HasPermission permission="product.index">
            <NavItem
              label="Master Data"
              Icon={Database}
              active={activePage === "master-data"}
              isCollapsed={effectiveCollapsed}
              to="/master-data/produk"
            />
          </HasPermission>
          <HasPermission permission={["report.index", "dashboard.view_stats"]}>
            <NavItem
              label="Laporan"
              Icon={BarChart3}
              active={activePage === "laporan"}
              isCollapsed={effectiveCollapsed}
              to="/laporan"
              state={{ featureName: "Laporan" }}
            />
          </HasPermission>
        </nav>

        {/* Bottom Config */}
        <div
          className="p-2.5 border-t border-white/10"
          onClick={handleNavClick}>
          <HasPermission permission={["profile.update", "pos.create_trx"]}>
            <NavItem
              label="Konfigurasi"
              Icon={Settings}
              active={activePage === "konfigurasi"}
              isCollapsed={effectiveCollapsed}
              to="/konfigurasi"
            />
          </HasPermission>
        </div>
      </div>
    </>
  );
}
